import { createApiRef, IdentityApi } from '@backstage/core-plugin-api';
import { RestClientApi } from '@runway/devkit-common';

// ── Types ─────────────────────────────────────────────────────────────────────

/** Response from validation */
interface ValidateAppShortNameResponse {
  isApproved: boolean;
  appShortName: string;
}

// ── Service interface & API ref ───────────────────────────────────────────────

/**
 * Service that checks whether an application short name
 * exists in OneTrust completed assessments.
 *
 * Delegates to the onetrust backend plugin for secure credential management.
 */
export interface OneTrustAssessmentServiceApi {
  /** Returns `true` if the app short name exists in any completed OneTrust assessment. */
  isAppShortNameApproved(appShortName: string): Promise<boolean>;
}

export const OneTrustAssessmentServiceApiRef = createApiRef<OneTrustAssessmentServiceApi>({
  id: 'plugin.devkit.onetrust-assessment.service',
});

// ── Service implementation ────────────────────────────────────────────────────

/**
 * Frontend service for OneTrust assessment validation.
 * Passes Backstage identity token to the backend plugin.
 */
export class OneTrustAssessmentService implements OneTrustAssessmentServiceApi {
  constructor(
    private readonly backendUrl: string,
    private readonly restClient: RestClientApi,
    private readonly identityApi: IdentityApi,
  ) {}

  async isAppShortNameApproved(appShortName: string): Promise<boolean> {
    const { token } = await this.identityApi.getCredentials();

    const { data } = await this.restClient.post<ValidateAppShortNameResponse>(
      `${this.backendUrl}/validate-app-shortname`,
      { appShortName },
      { headers: { Authorization: `Bearer ${token}` } },
    );

    return data.isApproved;
  }
}
